import {z} from 'zod';
import {createForm} from '../../forms';
import {DropdownOptionItem} from '../../types';
import {Stack} from '@mantine/core';

type Fields = {
  checkbox: boolean;
  checkboxGroup: boolean;
  primaryColor: string;
  secondaryColor: string;
  currency: number;
  datePicker: Date;
  dateTimePicker: Date;
  fileUri: string;
  maskedInput: string;
  multiselect: string[];
  numberInput: number;
  password: string;
  phone: string;
  pin: string;
  radio: string;
  radioGroup: string;
  rating: number;
  segmentedControl: string;
  select: string;
  slider: number;
  switch: boolean;
  textarea: string;
  textInput: string;
  time: string;
};
const fieldsSchema = z.object({
  checkbox: z.boolean(),
  checkboxGroup: z.boolean(),
  primaryColor: z.string(),
  secondaryColor: z.string(),
  currency: z.number(),
  datePicker: z.date(),
  dateTimePicker: z.date(),
  fileUri: z.string(),
  maskedInput: z.string(),
  multiselect: z.array(z.string()),
  numberInput: z.number(),
  password: z.string(),
  phone: z.string(),
  pin: z.string(),
  radio: z.string(),
  radioGroup: z.string(),
  rating: z.number(),
  segmentedControl: z.string(),
  select: z.string(),
  slider: z.number(),
  switch: z.boolean(),
  textarea: z.string(),
  textInput: z.string(),
  time: z.string(),
}) satisfies z.Schema<Fields>;
type FieldsSchema = z.infer<typeof fieldsSchema>;

const Form = createForm<FieldsSchema>();

const options: DropdownOptionItem[] = [
  {
    value: 'csharp',
    label: 'C#',
  },
  {
    value: 'javascript',
    label: 'JavaScript',
  },
  {
    value: 'java',
    label: 'Java',
  },
  {
    value: 'python',
    label: 'Python',
  },
];

export const FieldsDemo = () => {
  return (
    <Form schema={fieldsSchema}>
      <Form.Container>
        <Stack gap={'xs'}>
          <Form.Row>
            <Form.Checkbox name={'checkbox'} label="Checkbox" />
            <Form.CheckboxGroup name={'checkboxGroup'} label="Checkbox Group">
              {options.map((option, index) => (
                <Form.CheckboxGroup.Item
                  key={`checkbox-group-item-${index}`}
                  value={option.value}
                  label={option.label}
                />
              ))}
            </Form.CheckboxGroup>
            <Form.Radio name={'radio'} label="Radio" />
            <Form.RadioGroup name={'radioGroup'} label="Radio Group">
              {options.map((option, index) => (
                <Form.RadioGroup.Item
                  key={`radio-group-item-${index}`}
                  value={option.value}
                  label={option.label}
                />
              ))}
            </Form.RadioGroup>
          </Form.Row>
          <Form.Row>
            <Form.ColorInput
              name={'primaryColor'}
              label="Primary Color"
              placeholder={'Placeholder'}
            />
            <Form.ColorPicker name={'secondaryColor'} label="Secondary Color" />
          </Form.Row>
          <Form.Row>
            <Form.CurrencyInput name={'currency'} label="Currency" />
            <Form.DatePickerInput
              name={'datePicker'}
              label="Date Picker"
              placeholder={'Placeholder'}
            />
            <Form.DateTimePicker
              name={'dateTimePicker'}
              label="Date Time Picker"
              placeholder={'Placeholder'}
            />
          </Form.Row>
          <Form.Row>
            <Form.FileButton
              onChange={(v) => Promise.resolve(v?.name)}
              name={'fileUri'}
              label="File Upload"
            />
            <Form.MaskedInput
              name={'maskedInput'}
              label="Masked Input"
              placeholder={'Placeholder'}
              // mask="999-999-9999"
            />
            <Form.NumberInput
              name={'numberInput'}
              label="Number Input"
              placeholder={'Placeholder'}
            />
          </Form.Row>
          <Form.Row>
            <Form.PasswordInput
              name={'password'}
              label="Password"
              placeholder={'Placeholder'}
            />
            <Form.PhoneNumberInput
              name={'phone'}
              label="Phone"
              placeholder={'Placeholder'}
            />
            <Form.PinInput name={'pin'} label="Pin" />
          </Form.Row>
          <Form.Row>
            <Form.MultiSelect
              placeholder={'Placeholder'}
              name={'multiselect'}
              label="MultiSelect"
              data={options}
            />
            <Form.Select
              placeholder={'Placeholder'}
              name={'select'}
              label="Select"
              data={options}
            />
          </Form.Row>
          <Form.Row>
            <Form.Rating name={'rating'} label="Rating" />
            <Form.Slider name={'slider'} label="Slider" />
            <Form.Switch name={'switch'} label="Switch" />
            <Form.SegmentedControl
              name={'segmentedControl'}
              label="Segmented Control"
              data={options.map((x) => x.label)}
            />
          </Form.Row>
          <Form.Row>
            <Form.TimeInput name={'time'} label="Time" />
            <Form.TextInput
              name={'textInput'}
              label="Text Input"
              placeholder={'Placeholder'}
            />
            <Form.TextArea
              name={'textarea'}
              label="Textarea"
              placeholder={'Placeholder'}
            />
          </Form.Row>
          <Form.Row></Form.Row>
          <Form.Row></Form.Row>
        </Stack>
        <Form.Submit />
      </Form.Container>
    </Form>
  );
};